import { useState } from "react";
import Layout, { Section, PageHero } from "@/components/layout/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";

const topics = ["AI Transformation", "Voice AI", "Agentic Systems", "Product Partnership", "Research", "Other"];

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", organization: "", topic: "", message: "", website: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    // When this page loaded — sent to the backend so it can reject
    // submissions that arrive suspiciously fast (a bot signal).
    const [renderedAt] = useState(() => Date.now());

    const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please share your name, email, and a short message.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", { ...form, ts: renderedAt });
            setDone(true);
            toast.success("Message received. We will be in touch quietly.");
            setForm({ name: "", email: "", organization: "", topic: "", message: "", website: "" });
        } catch (err) {
            toast.error("Something went wrong. Please try again in a moment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout title="Contact — Ventara" description="Start a quiet conversation.">
            <PageHero i18nKey="contact"
                eyebrow="Contact"
                title={<>Begin quietly.</>}
                lead="We reply within two working days. Tell us what you are trying to make — no formal brief required."
            />

            <Section className="pb-32">
                <div className="grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-5 space-y-10">
                        <div>
                            <p className="label">Studio</p>
                            <p className="font-serif text-2xl mt-3 leading-tight">Ventara Studio<br />Remote-first · HQ in Lisbon</p>
                        </div>
                        <div>
                            <p className="label">Email</p>
                            <p className="font-serif text-2xl mt-3">hello@iventara.com</p>
                        </div>
                        <div>
                            <p className="label">Support</p>
                            <p className="font-serif text-2xl mt-3">support@iventara.com</p>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        {done ? (
                            <div className="border border-border rounded-3xl p-12" data-testid="contact-success">
                                <p className="label">Received</p>
                                <h2 className="font-serif font-light text-4xl mt-4 tracking-tight leading-tight">
                                    Thank you. We will read this carefully.
                                </h2>
                                <p className="prose-body mt-6 max-w-lg">
                                    Someone from the studio will be in touch within two working days. In the meantime, if it would help, our Insights are a good place to keep reading.
                                </p>
                                <button onClick={() => setDone(false)} data-testid="contact-reset" className="mt-8 text-sm border-b border-foreground/40 pb-1 hover:border-foreground transition-colors">
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
                                {/* Honeypot — invisible to real visitors (off-screen + aria-hidden so
                                    screen readers skip it too), but bots that auto-fill every field
                                    on the page will populate it. See create_contact() on the backend. */}
                                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
                                    <label htmlFor="website">Leave this field blank</label>
                                    <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off"
                                           value={form.website} onChange={on("website")} />
                                </div>
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <div>
                                        <Label htmlFor="name" className="label">Your name</Label>
                                        <Input id="name" data-testid="contact-name" value={form.name} onChange={on("name")}
                                               className="mt-3 h-12 bg-transparent border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-foreground text-lg font-serif" />
                                    </div>
                                    <div>
                                        <Label htmlFor="email" className="label">Email</Label>
                                        <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={on("email")}
                                               className="mt-3 h-12 bg-transparent border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-foreground text-lg font-serif" />
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="org" className="label">Organization</Label>
                                    <Input id="org" data-testid="contact-org" value={form.organization} onChange={on("organization")}
                                           className="mt-3 h-12 bg-transparent border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-foreground text-lg font-serif" />
                                </div>

                                <div>
                                    <Label className="label">What is this about?</Label>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {topics.map((t) => (
                                            <button
                                                key={t}
                                                type="button"
                                                data-testid={`topic-${t.toLowerCase().replace(/\s+/g,'-')}`}
                                                onClick={() => setForm((f) => ({ ...f, topic: t }))}
                                                className={`px-4 py-2 rounded-full text-xs border transition-colors ${
                                                    form.topic === t
                                                        ? "bg-foreground text-background border-foreground"
                                                        : "border-border hover:border-foreground/40"
                                                }`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="msg" className="label">Message</Label>
                                    <Textarea id="msg" data-testid="contact-message" rows={6} value={form.message} onChange={on("message")}
                                              className="mt-3 bg-transparent border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-foreground text-lg font-serif resize-none" />
                                </div>

                                <Button type="submit" data-testid="contact-submit" disabled={submitting}
                                        className="rounded-full h-12 px-8 bg-foreground text-background hover:bg-foreground/90 text-sm">
                                    {submitting ? "Sending…" : "Send message"}
                                </Button>
                            </form>
                        )}
                    </div>
                </div>
            </Section>
        </Layout>
    );
}
